/**
 * Hook for managing friend/block relationships
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Identity,
  DAGStore,
  DAGObject,
  RelationshipContent,
  addFriend,
  removeFriend,
  blockUser,
  unblockUser,
  processRelationships,
} from '@amorphous/core';

interface UseRelationshipsResult {
  friends: Set<string>;
  blocked: Set<string>;
  loading: boolean;
  addFriend: (pubkey: string) => Promise<void>;
  removeFriend: (pubkey: string) => Promise<void>;
  blockUser: (pubkey: string) => Promise<void>;
  unblockUser: (pubkey: string) => Promise<void>;
  isFriend: (pubkey: string) => boolean;
  isBlocked: (pubkey: string) => boolean;
  toggleFriend: (pubkey: string) => Promise<void>;
  toggleBlock: (pubkey: string) => Promise<void>;
  refreshRelationships: () => Promise<void>;
}

export function useRelationships(
  identity: Identity | null,
  store: DAGStore | null,
  broadcastObject?: (obj: DAGObject) => void
): UseRelationshipsResult {
  const [friends, setFriends] = useState<Set<string>>(new Set());
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Load relationships from store
  const refreshRelationships = useCallback(async () => {
    if (!store || !identity) {
      setLoading(false);
      return;
    }

    try {
      const all = await store.getByCreator(identity.publicKey);
      const relationships = all.filter(
        obj => obj.type === 'relationship'
      ) as DAGObject<RelationshipContent>[];

      const { friends: friendSet, blocked: blockedSet } = processRelationships(relationships);
      setFriends(friendSet);
      setBlocked(blockedSet);
    } catch (err) {
      console.error('[Relationships] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, [store, identity]);

  // Load on mount
  useEffect(() => {
    refreshRelationships();
  }, [refreshRelationships]);

  const handleAddFriend = useCallback(async (pubkey: string) => {
    if (!identity || !store) return;

    const obj = addFriend(identity, pubkey);
    await store.put(obj);
    setFriends(prev => new Set([...prev, pubkey]));

    if (broadcastObject) {
      broadcastObject(obj);
    }
  }, [identity, store, broadcastObject]);

  const handleRemoveFriend = useCallback(async (pubkey: string) => {
    if (!identity || !store) return;

    const obj = removeFriend(identity, pubkey);
    await store.put(obj);
    setFriends(prev => {
      const next = new Set(prev);
      next.delete(pubkey);
      return next;
    });

    if (broadcastObject) {
      broadcastObject(obj);
    }
  }, [identity, store, broadcastObject]);

  const handleBlockUser = useCallback(async (pubkey: string) => {
    if (!identity || !store) return;

    const obj = blockUser(identity, pubkey);
    await store.put(obj);
    setBlocked(prev => new Set([...prev, pubkey]));

    // Also remove from friends if they were a friend
    if (friends.has(pubkey)) {
      const unfriendObj = removeFriend(identity, pubkey);
      await store.put(unfriendObj);
      setFriends(prev => {
        const next = new Set(prev);
        next.delete(pubkey);
        return next;
      });
      if (broadcastObject) {
        broadcastObject(unfriendObj);
      }
    }

    if (broadcastObject) {
      broadcastObject(obj);
    }
  }, [identity, store, broadcastObject, friends]);

  const handleUnblockUser = useCallback(async (pubkey: string) => {
    if (!identity || !store) return;

    const obj = unblockUser(identity, pubkey);
    await store.put(obj);
    setBlocked(prev => {
      const next = new Set(prev);
      next.delete(pubkey);
      return next;
    });

    if (broadcastObject) {
      broadcastObject(obj);
    }
  }, [identity, store, broadcastObject]);

  const isFriend = useCallback((pubkey: string) => {
    return friends.has(pubkey);
  }, [friends]);

  const isBlocked = useCallback((pubkey: string) => {
    return blocked.has(pubkey);
  }, [blocked]);

  const toggleFriend = useCallback(async (pubkey: string) => {
    if (friends.has(pubkey)) {
      await handleRemoveFriend(pubkey);
    } else {
      await handleAddFriend(pubkey);
    }
  }, [friends, handleAddFriend, handleRemoveFriend]);

  const toggleBlock = useCallback(async (pubkey: string) => {
    if (blocked.has(pubkey)) {
      await handleUnblockUser(pubkey);
    } else {
      await handleBlockUser(pubkey);
    }
  }, [blocked, handleBlockUser, handleUnblockUser]);

  return {
    friends,
    blocked,
    loading,
    addFriend: handleAddFriend,
    removeFriend: handleRemoveFriend,
    blockUser: handleBlockUser,
    unblockUser: handleUnblockUser,
    isFriend,
    isBlocked,
    toggleFriend,
    toggleBlock,
    refreshRelationships,
  };
}
